import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import {
  Container, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Typography, Grid,
  TablePagination, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ViewEmployees = () => {
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [companyCountry, setCompanyCountry] = useState("India");
  const [bankCodeLabel, setBankCodeLabel] = useState("IFSC Code");
  
  const [searchField, setSearchField] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem("token"); // get JWT token
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
    
    if (company_id) {
      try {
        const companyResponse = await axios.get(`${API_URL}/api/cmp/${company_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (companyResponse.data.success && companyResponse.data.data?.country) {
          setCompanyCountry(companyResponse.data.data.country);
          if (companyResponse.data.data.bankCodeLabel) setBankCodeLabel(companyResponse.data.data.bankCodeLabel);
        }
      } catch (cErr) {
        // console.error("Error fetching company country:", cErr);
      }
    }

      setLoading(true);
      const response = await axios.get(`${API_URL}/api/viewallfinancial`,{ headers: {
        Authorization: `Bearer ${token}`
      }});
      

      const transformedData = response.data.map(emp => ({
        employeeDetails: {
          department: emp.department,
          employeeId: emp.employee_id,
          resignationDate: emp.resignationDate,
          noticePeriod: emp.noticePeriod,
          advanceSalary: emp.advanceSalary,
          creditCardOffered: emp.creditCardOffered,
        },
        finance: {
          bankName: emp.bankName,
          accountHolderName: emp.accountHolderName,
          accountNumber: emp.accountNumber,
          accountType: emp.accountType,
          branch: emp.branch,
          ifscCode: emp.ifscCode,
          currentSalary: emp.currentSalary,
          previousSalary: emp.previousSalary,
          ctc: emp.ctc,
          taxCalculation: emp.taxCalculation,
        },
        emergencyContact: {
          mobile: emp.mobile,
          landline: emp.landline,
        },
        healthInfo: {
          individualInsurance: emp.individualInsurance,
          groupInsurance: emp.groupInsurance,
        },

        updatedAt: emp.updatedAt,
        createdAt: emp.createdAt,
        id: emp.id
      }));

      const sortedData = transformedData.sort((a, b) => {
        const aUpdate = a.updatedAt ? new Date(a.updatedAt) : null;
        const bUpdate = b.updatedAt ? new Date(b.updatedAt) : null;

        const aCreate = a.createdAt ? new Date(a.createdAt) : new Date(0);
        const bCreate = b.createdAt ? new Date(b.createdAt) : new Date(0);

        if (aUpdate && bUpdate) {
          return bUpdate - aUpdate;
        }

        if (aUpdate) return -1;
        if (bUpdate) return 1;

        return bCreate - aCreate;
      });

      setEmployees(sortedData);
      setFilteredEmployees(sortedData);
    } catch (err) {
      setError("Failed to fetch employee data");
      //console.error("Error fetching employees:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 7));
    setPage(0);
  };

  const handleEdit = (employee) => {
    navigate("/addfinancial", {
      state: {
        isEdit: true,
        employee_id: employee.employeeDetails.employeeId,
        initialData: employee
      }
    });
  };


  const handleSearchFieldChange = (event) => {
    setSearchField(event.target.value);
    setSearchTerm('');
  };

  
  const getSearchPlaceholder = () => {
    switch(searchField) {
      case 'employeeId': return 'Search by Employee ID';
      case 'department': return 'Search by Department';
      case 'resignationDate': return 'Search by Resignation Date';
      case 'noticePeriod': return 'Search by Notice Period';
      case 'creditCardOffered': return 'Search by Credit Card Status';
      case 'ctc': return 'Search by CTC';
      case 'bankName': return 'Search by Bank Name';
      case 'accountHolderName': return 'Search by Account Holder Name';
      case 'accountType': return 'Search by Account Type';
      case 'branch': return 'Search by Branch';
      case 'individualInsurance': return 'Search by Individual Insurance';
      case 'groupInsurance': return 'Search by Group Insurance';
      
    }
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setFilteredEmployees(employees);
      return;
    }

    const searchTermLower = searchTerm.toLowerCase();
    
    const filtered = employees.filter(employee => {
     
      if (searchField === 'all') {
        const employeeIdMatch = employee?.employeeDetails?.employeeId?.toString().toLowerCase().includes(searchTermLower);
        const departmentMatch = employee?.employeeDetails?.department?.toLowerCase().includes(searchTermLower);
        const noticePeriodMatch = employee?.employeeDetails?.noticePeriod?.toLowerCase().includes(searchTermLower);
        const resignationDateMatch = employee?.employeeDetails?.resignationDate?.toLowerCase().includes(searchTermLower);
        const creditCardMatch = employee?.employeeDetails?.creditCardOffered?.toLowerCase().includes(searchTermLower);
        const bankNameMatch = employee?.finance?.bankName?.toLowerCase().includes(searchTermLower);
        const accountHolderNameMatch = employee?.finance?.accountHolderName?.toLowerCase().includes(searchTermLower);
        const accountTypeMatch = employee?.finance?.accountType?.toLowerCase().includes(searchTermLower);
        const branchMatch = employee?.finance?.branch?.toLowerCase().includes(searchTermLower);
        const ctcMatch = employee?.finance?.ctc?.toString().toLowerCase().includes(searchTermLower);
        
        return employeeIdMatch || departmentMatch || noticePeriodMatch || 
               resignationDateMatch || creditCardMatch || bankNameMatch || accountHolderNameMatch || accountTypeMatch || branchMatch || ctcMatch;
      }
      
     
      switch(searchField) {
        case 'employeeId':
          return employee?.employeeDetails?.employeeId?.toString().toLowerCase().includes(searchTermLower);
        case 'department':
          return employee?.employeeDetails?.department?.toLowerCase().includes(searchTermLower);
        case 'resignationDate':
          return employee?.employeeDetails?.resignationDate?.toLowerCase().includes(searchTermLower);
        case 'noticePeriod':
          return employee?.employeeDetails?.noticePeriod?.toLowerCase().includes(searchTermLower);
        case 'creditCardOffered':
          return employee?.employeeDetails?.creditCardOffered?.toLowerCase().includes(searchTermLower);
        case 'ctc':
          return employee?.finance?.ctc?.toString().toLowerCase().includes(searchTermLower);
        case 'bankName':
          return employee?.finance?.bankName?.toLowerCase().includes(searchTermLower);
        case 'accountHolderName':
          return employee?.finance?.accountHolderName?.toLowerCase().includes(searchTermLower);
        case 'accountType':
          return employee?.finance?.accountType?.toLowerCase().includes(searchTermLower);
        case 'branch':
          return employee?.finance?.branch?.toLowerCase().includes(searchTermLower);
      
        default:
          return false;
      }
    });
    
    setFilteredEmployees(filtered);
    setPage(0);
  };

  const paginatedEmployees = filteredEmployees.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" gutterBottom align="center" fontWeight="bold">Employee List</Typography>

      <Grid container spacing={2} alignItems="center" sx={{ mb: 2}}>
        
        <Grid item xs={12} md={2}>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="search-field-label">Search Field</InputLabel>
            <Select
              labelId="search-field-label"
              id="search-field"
              value={searchField}
              onChange={handleSearchFieldChange}
              label="Search Field"
            >
            
              <MenuItem value="employeeId">Employee ID</MenuItem>
              <MenuItem value="department">Department</MenuItem>
              <MenuItem value="resignationDate">Resignation Date</MenuItem>
              <MenuItem value="noticePeriod">Notice Period</MenuItem>
              <MenuItem value="creditCardOffered">Credit Card Offered</MenuItem>
              <MenuItem value="ctc">CTC</MenuItem>
              <MenuItem value="bankName">Bank Name</MenuItem>
              <MenuItem value="accountHolderName">Account Holder Name</MenuItem>
              <MenuItem value="accountType">Account Type</MenuItem>
              <MenuItem value="branch">Branch</MenuItem>
          
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={8}>
          <TextField
            label={getSearchPlaceholder()}
            variant="outlined"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
            startIcon={<SearchIcon />}
            fullWidth
           
          >
            Search
          </Button>
        </Grid>
      </Grid>

      <div style={{ position: 'relative' }}>
        <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 150 }}><strong>Employee ID</strong></TableCell>
                <TableCell sx={{ minWidth: 150 }}><strong>Department</strong></TableCell>
                 <TableCell sx={{ minWidth: 150 }}><strong>Bank Name</strong></TableCell>
                <TableCell sx={{ minWidth: 180 }}><strong>Account Holder Name</strong></TableCell>
                <TableCell sx={{ minWidth: 150 }}><strong>Account Number</strong></TableCell>
                <TableCell sx={{ minWidth: 150 }}><strong>Account Type</strong></TableCell>
                <TableCell sx={{ minWidth: 150 }}><strong>Branch</strong></TableCell>
                <TableCell sx={{ minWidth: 150 }}><strong>{bankCodeLabel}</strong></TableCell>
                <TableCell sx={{ minWidth: 150 }}><strong>Current Salary</strong></TableCell>
                <TableCell sx={{ minWidth: 150 }}><strong>Previous Salary</strong></TableCell>
                <TableCell sx={{ minWidth: 150 }}><strong>CTC</strong></TableCell>
                <TableCell sx={{ minWidth: 150 }}><strong>Tax Calculation</strong></TableCell>
                <TableCell sx={{ minWidth: 150 }}><strong>Resignation Date</strong></TableCell>
                <TableCell sx={{ minWidth: 150 }}><strong>Notice Period</strong></TableCell>
                <TableCell sx={{ minWidth: 150 }}><strong>Advance Salary</strong></TableCell>
                <TableCell sx={{ minWidth: 200 }}><strong>Credit Card Offered</strong></TableCell>
               
               
                <TableCell sx={{ minWidth: 200 }}><strong>Last Updated</strong></TableCell>
                <TableCell sx={{ minWidth: 200 }}><strong>Created At</strong></TableCell>
                {/* <TableCell>Actions</TableCell> */}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedEmployees.length > 0 ? (
                paginatedEmployees.map((employee) => (
                  <TableRow key={employee.id} style={{ position: 'relative' }}>
                    <TableCell
                     style={{ 
                      color: "#1976d2", 
                      cursor: "pointer", 
                      textDecoration: "underline" 
        }}
                    onClick={()=>handleEdit(employee)}
                    >{employee.employeeDetails.employeeId || "Not Available"}</TableCell>
                    <TableCell>{employee.employeeDetails.department?.trim() || "Not Provided"}</TableCell>
                    <TableCell>{employee.finance.bankName || "Not Mentioned"}</TableCell>
                    <TableCell>{employee.finance.accountHolderName || "N/A"}</TableCell>
                    <TableCell>{employee.finance.accountNumber || "N/A"}</TableCell>
                    <TableCell>{employee.finance.accountType || "N/A"}</TableCell>
                    <TableCell>{employee.finance.branch || "N/A"}</TableCell>
                    <TableCell>{employee.finance.ifscCode || "N/A"}</TableCell>
                    <TableCell>{employee.finance.currentSalary || "0.00"}</TableCell>
                    <TableCell>{employee.finance.previousSalary || "0.00"}</TableCell>
                    <TableCell>{employee.finance.ctc || "N/A"}</TableCell>
                    <TableCell>{employee.finance.taxCalculation || "N/A"}</TableCell>
                    <TableCell>{employee.employeeDetails.resignationDate || "Not Provided"}</TableCell>
                    <TableCell>{employee.employeeDetails.noticePeriod || "Not Provided"}</TableCell>
                    <TableCell>{employee.employeeDetails.advanceSalary || "N/A"}</TableCell>
                    <TableCell>{employee.employeeDetails.creditCardOffered || "N/A"}</TableCell>
                  
                   
                    <TableCell>{employee.updatedAt ? new Date(employee.updatedAt).toLocaleString() : "N/A"}</TableCell>
                    <TableCell>{employee.createdAt ? new Date(employee.createdAt).toLocaleString() : "N/A"}</TableCell>
                    {/* <TableCell
                      style={{
                        position: 'sticky',
                        right: 0,
                        backgroundColor: 'white',
                        zIndex: 1,
                      }}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleEdit(employee)}
                       
                      >
                        Edit
                      </Button>
                    </TableCell> */}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={19} align="center">
                    No employees found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[7]}
            component="div"
            count={filteredEmployees.length}
            rowsPerPage={7}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </div>
    </Container>
  );
};

export default ViewEmployees;